
import { Component, Vue, Ref } from 'vue-property-decorator'
import SelectStudyCategoryContentList from '@/components/templates/SelectStudyCategoryContentList.vue'
import ModalSelectGeneralStudyCategory from '@/views/modal/ModalSelectGeneralStudyCategory.vue'
import CurriculumUnitFilters from '@/utils/CurriculumUnitFilters'
import { ProgressFilter } from '@/const/ProgressFilter'
import { SubjectCode } from '@/const/SubjectCode'
import { GradeCode } from '@/const/GradeCode'
import { StudyCategoryCurriculumUnit } from '@/models/CurriculumUnit'
import { CurriculumUnitsApi } from '@/api/methods/CurriculumUnitsApi'
import { StudyCategoriesApi } from '@/api/methods/StudyCategoriesApi'
import { LearningProgress } from '@/const/LearningProgress'
import { StudyCategory } from '@/models/StudyCategory'

/**
 * 通常授業サポート学習
 */
@Component({
  components: {
    SelectStudyCategoryContentList,
    ModalSelectGeneralStudyCategory,
  },
})
export default class GeneralStudy extends Vue {
  /** 学習教材選択ページレイアウト */
  @Ref()
  selectStudyCategoryContentList!: SelectStudyCategoryContentList

  /** 通常授業サポート教材選択モーダル */
  @Ref()
  modalSelectGeneralStudyCategory!: ModalSelectGeneralStudyCategory

  /** 選択中の教科コード */
  private currentSubjectCode: string = SubjectCode.MATH.code

  /** 選択中の学年コード */
  private currentGradeCode: string = GradeCode.LIST[0].code

  /** 学習教材のリスト */
  private studyCategoryList: StudyCategory.General[] = []

  /** 選択中の学習教材ID */
  private currentStudyCategoryId: number | null = null

  /** 学習進捗選択ボタンの初期値 */
  private defaultProgressFilterCode: string = ProgressFilter.LIST[0].code

  /** 学習教材用単元情報のリスト */
  private studyCategoryCurriculumUnitList: StudyCategoryCurriculumUnit[] = []

  /**
   * 選択中の学習教材情報を引き当てる
   *
   * @returns 学習教材情報
   */
  private get currentStudyCategory(): StudyCategory.General | undefined {
    return this.studyCategoryList.find((studyCategory) => {
      return studyCategory.id === this.currentStudyCategoryId
    })
  }

  /**
   * 選択中の学習教材名
   *
   * @returns 教材名
   */
  private get currentStudyCategoryName(): string {
    const studyCategory = this.currentStudyCategory
    // undefinedケース 発生しないがブロックの為記述
    if (!studyCategory) {
      return ''
    }
    return studyCategory.name
  }

  /**
   * 教材画像URL
   *
   * @returns 教材イメージのパス
   */
  private get textbookImagePath(): string | null {
    const studyCategory = this.currentStudyCategory
    // undefinedケース 発生しないがブロックの為記述
    if (!studyCategory) {
      return null
    }
    return studyCategory.textbookImagePath
  }

  /**
   * 学習教材用単元情報のリストを学習進捗でフィルタリングする
   *
   * @returns フィルタリングされた単元教材用単元情報のリスト
   */
  private get filterStudyCategoryCurriculumUnitList(): StudyCategoryCurriculumUnit[] {
    return CurriculumUnitFilters.byProgressFilter(
      this.studyCategoryCurriculumUnitList,
      this.currentProgressFilterCode()
    )
  }

  /**
   * 選択中の学習進捗コード
   *
   * @returns 学習進捗コード
   */
  private currentProgressFilterCode(): string {
    // DOM未処理によるundefinedブロック
    if (!this.selectStudyCategoryContentList) {
      return ''
    }
    return this.selectStudyCategoryContentList.currentProgressFilterCode()
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()
    // クッキーから学年と教科情報を取得
    this.currentSubjectCode = await this.$appCookies.studentMenuState.subjectCode.get()
    this.currentGradeCode = await this.$appCookies.studentMenuState.gradeCode.get()

    // 学習教材一覧情報の設定
    await this.getStudyCategoryData()

    // 学習教材が0件だった場合、処理を終了
    if (this.studyCategoryList.length === 0) {
      // URLにクエリ情報が残っている場合はクリアする
      if (this.$route.query.studyCategoryId) {
        await this.$router.replace(this.$router.currentRoute.path)
      }
      this.$loading.complete()
      return
    }

    // URLのクエリ情報から教材IDを取得
    const studyCategoryId = this.$route.query.studyCategoryId

    // 取得した教材一覧から、教材IDの配列を作成
    const idList = this.studyCategoryList.map((studyCategory) => studyCategory.id)

    if (!studyCategoryId || !idList.includes(Number(studyCategoryId))) {
      // クエリの教材IDがない、もしくは教材一覧に該当の教材IDがない場合の処理
      this.currentStudyCategoryId = this.studyCategoryList[0].id
      await this.$router.replace({
        query: { studyCategoryId: `${this.currentStudyCategoryId}` },
      })
    } else {
      // クエリの教材IDを取得した場合の処理
      this.currentStudyCategoryId = Number(studyCategoryId)
    }

    // 教材に紐づく単元一覧の設定
    await this.getStudyCategoryCurriculumUnitList()

    this.$loading.complete()
  }

  /**
   * 学習教材一覧を設定
   */
  private async getStudyCategoryData(): Promise<void> {
    // 教材情報取得
    const studyCategories = await StudyCategoriesApi.getGenerals(this.currentGradeCode, this.currentSubjectCode)

    // front側の型に成形
    this.studyCategoryList = studyCategories.map((studyCategory) => {
      return {
        id: studyCategory.id,
        code: studyCategory.code,
        name: studyCategory.name,
        textbookImagePath: studyCategory.textbookImagePath,
      }
    })
  }

  /**
   * 教材に紐づく単元一覧を設定
   */
  private async getStudyCategoryCurriculumUnitList(): Promise<void> {
    // nullブロックのため記述(実際にはnullの際は呼ばれない)
    if (!this.currentStudyCategoryId) {
      return
    }

    // 学習教材に紐づく単元の一覧を取得
    const curriculumUnits = await CurriculumUnitsApi.getGenerals(this.currentStudyCategoryId)
    // front側の型に成形
    this.studyCategoryCurriculumUnitList = curriculumUnits.map((curriculumUnit) => {
      const progress = LearningProgress.findByCode(curriculumUnit.progress)
      return {
        id: curriculumUnit.id,
        name: curriculumUnit.name,
        learningProgress: progress,
        viewNumber: curriculumUnit.viewNumber,
      }
    })
  }

  /**
   * 通常授業サポート学習教材選択ボタン押下時のイベント
   */
  private onSelectGenaralStudyCategoryButton(): void {
    if (this.currentStudyCategoryId === null) {
      return
    }
    this.modalSelectGeneralStudyCategory.show(`${this.currentStudyCategoryId}`)
  }

  /**
   * 確認ボタン押下時のイベント
   *
   * @param id 学習教材ID
   */
  private async eventSubmitStudyCategory(id: number): Promise<void> {
    // 選択値が現在の教材IDと同じなら終了
    if (this.currentStudyCategoryId === id) {
      return
    }

    this.$loading.start()
    // 選択値を現在の教材IDに設定
    this.currentStudyCategoryId = id
    // クエリ情報の更新
    await this.$router.replace({
      query: { studyCategoryId: `${this.currentStudyCategoryId}` },
    })
    // 単元情報取得
    await this.getStudyCategoryCurriculumUnitList()
    this.$loading.complete()
  }

  /**
   * 単元パネル押下時のイベント
   *
   * @param curriculumUnitId 選択した単元ID
   */
  private eventClickCurriculumUnit(curriculumUnitId: number): void {
    this.$router.push({
      path: `/student/general/${curriculumUnitId}`,
      query: { studyCategoryId: `${this.currentStudyCategoryId}` },
    })
  }
}
